import { Box, Stack, SxProps } from '@mui/material';
import {
  DocumentQuestion,
  TextQuestion
} from 'pages/CampaignCreation/components/QuestionsGroup';
import { useQuestionsHandler } from 'pages/CampaignCreation/hooks/useQuestionsHandler';
import { QuestionWithId } from 'pages/CampaignCreation/types';
import React, { useState } from 'react';
import { QuestionType } from 'services/campaign/types';
import AddIcon from '@mui/icons-material/Add';
import { CampaignCreationNextButton } from 'modules/campaign-creation/components/CampaignCreationNextButton';
import { Header } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { CampaignCreationClearQuestionsButton } from 'pages/CampaignCreation/components/CampaignCreationClearQuestionsButton';
import { CampaignCreationJobDescriptionButton } from 'pages/CampaignCreation/components/CampaignCreationJobDescriptionButton';
import { useSaveStateCampaign } from 'modules/campaign-creation/hooks/useSaveStateCampaign';
import { getTitle } from 'modules/campaign-creation/utils/getTitle';
import { AlertCard } from 'common/ui';
import { Button } from 'system/atoms';

const buttonSx: SxProps = {
  px: '4px'
};

const validateQuestions = (questions: QuestionWithId[]) => {
  // First check if there are any questions at all
  if (questions.length === 0) {
    return false;
  }

  const isTextQuestionWithContent = questions
    .filter((q) => q?.questionType === QuestionType.TEXT)
    .every((q) => !!q.content);

  const questionDocuments = questions.filter(
    (q) => q?.questionType === QuestionType.DOCUMENT
  );

  const isDocumentQuestionWithDocuments =
    questionDocuments.length > 0
      ? questionDocuments.every((q) => !!q.documentTypes?.length)
      : true;

  return isTextQuestionWithContent && isDocumentQuestionWithDocuments;
};

export const CampaignCreationQuestionsAndDocumentsStep: React.FC = () => {
  const state = useCampaignCreationStore((state) => state.state);
  const storeQuestions = useCampaignCreationStore(
    (state) => state.state?.questions
  );
  const noATSJobDescriptionText = useCampaignCreationStore(
    (state) => state.state?.noATSJobDescriptionState?.jobDescriptionText
  );
  const ATSJobDescriptionText = useCampaignCreationStore(
    (state) =>
      state.state?.ATSJobDescriptionState?.selectedJob.jobDescriptionText
  );

  const jobDescriptionParsed = useCampaignCreationStore(
    (state) => state.state?.jobDescriptionParsedState?.jobDescriptionParsed
  );
  const onQuestionsAndDocumentsBack = useCampaignCreationStore(
    (state) => state.onQuestionsAndDocumentsBack
  );
  const onQuestionsAndDocumentsSubmit = useCampaignCreationStore(
    (state) => state.onQuestionsAndDocumentsSubmit
  );
  const onEditJobDescription = useCampaignCreationStore(
    (state) => state.onEditJobDescription
  );
  const [questions, setQuestions] = useState<QuestionWithId[]>(
    storeQuestions ?? []
  );

  useSaveStateCampaign({
    questions
  });

  const { addQuestion, removeQuestion, moveQuestion, updateQuestion } =
    useQuestionsHandler({
      questionsWithId: questions,
      setQuestions
    });

  const handleNext = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    if (!validateQuestions(questions)) {
      return;
    }

    onQuestionsAndDocumentsSubmit({ questions });
  };

  return (
    <>
      <Header
        currentBreadcrumb={getTitle(state)}
        title="Screening questions"
        breadcrumbs={[{ name: 'Campaigns', path: Paths.CAMPAIGNS }]}
        onBack={onQuestionsAndDocumentsBack}
        actions={[
          <CampaignCreationClearQuestionsButton
            onClearQuestions={() => setQuestions([])}
          />,
          ...[
            jobDescriptionParsed ? (
              <CampaignCreationJobDescriptionButton
                jobDescriptionText={
                  noATSJobDescriptionText || ATSJobDescriptionText || ''
                }
                jobDescriptionParsed={jobDescriptionParsed}
                onSave={(jobDescriptionText) => {
                  onEditJobDescription({ jobDescriptionText });
                }}
              />
            ) : undefined
          ]
        ]}
      />
      <Box sx={{ maxWidth: '700px' }}>
        <Stack gap="30px">
          {questions.map((question, index) => {
            const { id, questionType } = question;
            const isFirst = index === 0;
            const isLast = index === questions.length - 1;

            // Render different question types based on questionType
            if (questionType === QuestionType.DOCUMENT) {
              return (
                <DocumentQuestion
                  key={id}
                  question={question}
                  moveQuestion={moveQuestion}
                  updateQuestion={updateQuestion}
                  removeQuestion={removeQuestion}
                  isFirst={isFirst}
                  isLast={isLast}
                />
              );
            }

            // Text questions (both knockout and qualifying) use unified TextQuestion
            return (
              <TextQuestion
                key={id}
                question={question}
                moveQuestion={moveQuestion}
                updateQuestion={updateQuestion}
                removeQuestion={removeQuestion}
                isFirst={isFirst}
                isLast={isLast}
              />
            );
          })}
          <Stack direction="row" gap="15px">
            <Button
              variant="tertiary"
              startIcon={<AddIcon />}
              onClick={() => addQuestion('qualifying')}
              sx={buttonSx}
            >
              Ask question
            </Button>
            <Button
              variant="tertiary"
              startIcon={<AddIcon />}
              onClick={() => addQuestion('document')}
              sx={buttonSx}
            >
              Ask for documents
            </Button>
          </Stack>
        </Stack>
        <Stack direction="column" gap="40px" sx={{ mt: 4 }}>
          <AlertCard
            type="info"
            title="Questions cannot be edited once this campaign is live"
          >
            Check your questions carefully before continuing.
          </AlertCard>
          <CampaignCreationNextButton
            handleNext={handleNext}
            isError={questions.length === 0}
            errorTitle="Nothing added"
            errorMessage="Add a question or document request."
          />
        </Stack>
      </Box>
    </>
  );
};
